<template>
  <div
    class="relative inline-block text-left text-black"
    v-click-away="closeMenu"
  >
    <div>
      <span class="rounded-md shadow-sm">
        <button
          @click="isMenuOpen = !isMenuOpen"
          type="button"
          class="inline-flex items-center justify-between w-full rounded-md h-10 px-4 py-4 bg-primary text-white hover:text-gray-200 transition ease-in-out duration-150"
          id="options-menu"
          aria-haspopup="true"
          aria-expanded="true"
        >
          <span v-if="profile && profile.first_name">
            {{ profile && `${profile.first_name} ${profile.last_name}` }}
          </span>
          <div v-else>
            <div class="animate-pulse flex space-x-4 rounded rounded-md">
              <div class="rounded-full bg-blue-100 h-5 w-52"></div>
              <div class="flex-1 space-y-6 py-1"></div>
            </div>
          </div>
          <div class="pl-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>
      </span>
    </div>
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isMenuOpen"
        class="origin-top-right absolute right-0 mt-2 w-96 rounded-2xl shadow-2xl text-sm overflow-hidden z-20"
      >
        <div
          class="rounded-md bg-white w-full px-2 py-3"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          <div>
            <div class="p-4 flex items-center">
              <div class="w-full">
                <div class="flex flex-row">
                  <div v-if="profile.avatar?.url" class="">
                    <div
                      class="m-1 w-12 h-12 relative flex justify-center items-center rounded-full text-xl text-white uppercase bg-cover"
                      :style="`background-image: url(${profile.avatar?.url})`"
                    ></div>
                  </div>
                  <div
                    v-else
                    class="flex justify-center items-center rounded-full bg-primary text-white w-12 h-12 p-4"
                  >
                    {{
                      getInitials(
                        profile && `${profile.first_name} ${profile.last_name}`
                      )
                    }}
                  </div>
                  <div class="flex flex-col pl-3 py-1">
                    <span class="font-semibold text-base">{{
                      profile && `${profile.first_name} ${profile.last_name}`
                    }}</span>
                    <span class="text-sm text-gray-500">{{
                      profile && profile.email
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col mt-4">
            <button
              @click="router.push('/account-settings/settings')"
              class="rounded p-4 hover:bg-background"
            >
              <div class="flex flex-row items-center">
                <img src="../../assets/navbar-icons/settings.svg" />
                <span class="font-semibold ml-4">Account settings</span>
              </div>
            </button>
            <div class="h-0.5 bg-gray-200 mx-4 my-2"></div>
            <button
              v-if="!isSourcingRecruiter"
              @click="router.push('/account-settings/members')"
              class="rounded p-4 hover:bg-background"
            >
              <div class="flex flex-row items-center">
                <img src="../../assets/navbar-icons/dribbble.svg" />
                <span class="font-semibold ml-4">Account members</span>
              </div>
            </button>
            <div
              v-if="!isSourcingRecruiter"
              class="h-0.5 bg-gray-200 mx-4 my-2"
            ></div>
            <button
              v-if="!isSourcingRecruiter"
              @click="router.push('/account-settings/billing')"
              class="rounded p-4 hover:bg-background"
            >
              <div class="flex flex-row items-center">
                <img src="../../assets/navbar-icons/dollar.svg" />
                <span class="font-semibold ml-4">Billing</span>
              </div>
            </button>
            <div
              v-if="!isSourcingRecruiter"
              class="h-0.5 bg-gray-200 mx-4 my-2"
            ></div>
            <button
              @click="router.push('/account-settings/notifications')"
              class="rounded p-4 hover:bg-background"
            >
              <div class="flex flex-row items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <span class="font-semibold ml-4">Notifications</span>
              </div>
            </button>

            <div class="h-0.5 bg-gray-200 mx-4 my-2"></div>
            <button @click="logOut" class="rounded p-4 hover:bg-background">
              <div class="flex flex-row items-center">
                <img src="../../assets/navbar-icons/logout.svg" />
                <span class="font-semibold ml-4">Log out</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getInitials } from "../../utils/functions";

const MenuDropDown = defineComponent({
  name: "MenuDropdown",
  emits: ["logOut"],
  setup(props, context) {
    const router = useRouter();
    const isMenuOpen = ref(false);
    const store = useStore();
    const profile = computed(() => store.state.profile.data);
    const roles = computed(() => store.state.profile.data.roles);
    const isSourcingRecruiter = computed(() =>
      store.state.profile.data.roles.includes("sourcing_recruiter")
    );
    const logOut = () => {
      context.emit("logOut");
    };
    const closeMenu = () => {
      isMenuOpen.value = false;
    };

    const changePlatform = () => {
      window.location.replace(process.env.VUE_APP_WORKFORCE_SOLUTIONS_URL);
    };

    return {
      isMenuOpen,
      roles,
      closeMenu,
      router,
      logOut,
      profile,
      getInitials,
      changePlatform,
      isSourcingRecruiter,
    };
  },
});

export default MenuDropDown;
</script>
